import { registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import localeCs from '@angular/common/locales/cs';
import { ErrorHandler, inject, LOCALE_ID, NgModule, provideAppInitializer } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { Router } from '@angular/router';
import { APP_CONFIG, AppConfig } from '@isophi/core-ui';
import { NgbModalConfig } from '@ng-bootstrap/ng-bootstrap';
import * as sentry from '@sentry/angular';
import { NgxGoogleAnalyticsModule, NgxGoogleAnalyticsRouterModule } from 'ngx-google-analytics';

import { environment } from '../environments/environment';
import { ApiModule } from './api';
import { ApiInterceptor } from './api/api-interceptor';
import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';
import { CoreModule } from './core/core.module';
import { InitService } from './core/services/init.service';
import { ChildrenModule } from './modules/children/children.module';
import { DashboardModule } from './modules/dashboard/dashboard.module';
import { HelpModule } from './modules/help/help.module';
import { HomeModule } from './modules/home/home.module';
import { LayoutModule } from './modules/layout/layout.module';
import { VerificationModule } from './modules/verification/verification.module';
import { SharedModule } from './shared/shared.module';
import { TranslocoRootModule } from './shared/transloco-root.module';

registerLocaleData(localeCs, 'cs');

export function initApplication(initService: InitService) {
  return () => initService.initApplication();
}

@NgModule({
  declarations: [AppComponent],
  imports: [
    ApiModule.forRoot(),
    AppRoutingModule,
    BrowserModule,
    BrowserAnimationsModule,
    CoreModule,
    TranslocoRootModule,
    SharedModule.forRoot(),
    HttpClientModule,
    DashboardModule,
    ChildrenModule,
    NgxGoogleAnalyticsModule.forRoot(environment.ga_measurement_id),
    NgxGoogleAnalyticsRouterModule,
    LayoutModule,
    HomeModule,
    VerificationModule,
    HelpModule,
  ],
  providers: [
    {
      provide: ErrorHandler,
      useValue: sentry.createErrorHandler(),
    },
    {
      provide: sentry.TraceService,
      deps: [Router],
    },
    { provide: LOCALE_ID, useValue: 'cs' },
    provideAppInitializer(() => {
      inject(sentry.TraceService);
      const initializerFn = initApplication(inject(InitService));
      return initializerFn();
    }),
    {
      provide: APP_CONFIG,
      useValue: {
        i18n: environment.i18n,
      } as AppConfig,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ApiInterceptor,
      multi: true,
    },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {
  constructor(modalConfig: NgbModalConfig) {
    modalConfig.centered = true;
    modalConfig.scrollable = true;
  }
}
